import axios from 'axios';

export function useApi() {
    const api = axios.create({
        baseURL: '/api',
        withCredentials: true,
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
        }
    });

    // Await the CSRF cookie before every request.
    // axios.create() instances do not inherit global interceptors from bootstrap.js.
    api.interceptors.request.use(async (config) => {
        await window.csrfReady;
        return config;
    });

    // Add response interceptor for error handling
    api.interceptors.response.use(
        (response) => response,
        async (error) => {
            // 419 CSRF mismatch — refresh XSRF-TOKEN cookie and retry once.
            // Happens when the session expires or is regenerated (e.g. after logout).
            if (error.response?.status === 419 && !error.config._csrfRetry) {
                error.config._csrfRetry = true;
                window.csrfReady = axios.get('/sanctum/csrf-cookie').catch(() => {});
                await window.csrfReady;
                return api(error.config);
            }

            if (error.response) {
                // Server responded with error status
                const { status, data } = error.response;

                if (status === 401) {
                    // Unauthorized - redirect to login
                    window.location.href = '/login';
                } else if (status === 403) {
                    // Forbidden
                    error.message = data.message || 'You do not have permission to perform this action';
                } else if (status === 404) {
                    error.message = data.message || 'Resource not found';
                } else if (status === 422) {
                    // Validation error
                    error.message = data.message || 'Validation failed';
                    error.errors = data.errors || {};
                } else if (status === 500) {
                    error.message = data.message || 'Internal server error';
                }
            } else if (error.request) {
                // Request made but no response
                error.message = 'No response from server. Please check your connection.';
            } else {
                // Something else happened
                error.message = error.message || 'An unexpected error occurred';
            }

            return Promise.reject(error);
        }
    );

    return {
        api,
        get: (url, config) => api.get(url, config),
        post: (url, data, config) => api.post(url, data, config),
        put: (url, data, config) => api.put(url, data, config),
        patch: (url, data, config) => api.patch(url, data, config),
        delete: (url, config) => api.delete(url, config)
    };
}
